import { useForm } from "react-hook-form";
import {
    Box, Button, Grid, TextField, Typography, IconButton,
    InputAdornment, LinearProgress, CircularProgress, Alert,
} from "@mui/material";
import { useState, useEffect } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import { useNavigate } from "react-router-dom";

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)',
        backgroundColor: 'var(--bg-subtle)', transition: 'all 0.2s ease',
        '& fieldset': { border: '2px solid transparent' },
        '&:hover fieldset': { border: '2px solid var(--border-hover)' },
        '&.Mui-focused fieldset': { border: '2px solid var(--accent)' },
        '&.Mui-focused': { backgroundColor: 'var(--bg-card)' },
    },
    '& .MuiInputLabel-root': { fontFamily: 'var(--font-body)', fontSize: '0.88rem', '&.Mui-focused': { color: 'var(--accent)' } },
    '& .MuiFormHelperText-root': { fontFamily: 'var(--font-body)', fontSize: '0.7rem' },
};

const ChangePass = ({ update, logout }) => {
    const [msgPass, setMsgPass] = useState("");
    const [msgColor, setMsgColor] = useState("success");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);

    const [showCliPass, setShowCliPass] = useState(false);
    const [showNewPass, setShowNewPass] = useState(false);
    const [showConfirmPass, setShowConfirmPass] = useState(false);

    const [passwordStrength, setPasswordStrength] = useState(0);
    const [passwordStrengthLabel, setPasswordStrengthLabel] = useState("");

    const { register, handleSubmit, watch, reset, formState: { errors } } = useForm({ mode: "onBlur" });
    const navigate = useNavigate();
    const passwordValue = watch("pass");

    useEffect(() => {
        if (!passwordValue) { setPasswordStrength(0); setPasswordStrengthLabel(""); return; }
        let s = 0;
        if (passwordValue.length >= 8) s++;
        if (/[A-Z]/.test(passwordValue)) s++;
        if (/[a-z]/.test(passwordValue)) s++;
        if (/\d/.test(passwordValue)) s++;
        if (/[\W_]/.test(passwordValue)) s++;
        setPasswordStrength(s);
        if (s <= 2) setPasswordStrengthLabel("Débil");
        else if (s <= 4) setPasswordStrengthLabel("Media");
        else setPasswordStrengthLabel("Fuerte");
    }, [passwordValue]);

    const getStrengthColor = () => {
        if (passwordStrength <= 2) return "error";
        if (passwordStrength <= 4) return "warning";
        return "success";
    };

    const getStrengthCSSColor = () => {
        if (passwordStrength <= 2) return "var(--danger)";
        if (passwordStrength <= 4) return "var(--warning)";
        return "var(--success)";
    };

    const handleChangePassword = async (data) => {
        setIsSubmitted(true);
        const body = { current_pass: data.cli_pass, cli_pass: data.pass_confirm };
        try {
            const res = await update(body);
            if (res?.data?.msg?.includes("correctamente")) {
                setMsgColor("success");
                setMsgPass("La contraseña ha sido actualizada con éxito. Serás desconectado en 5 segundos.");
            } else if (res?.response?.data?.error) {
                setMsgColor("error");
                setMsgPass(`${res.response.data.error}. Serás desconectado en 5 segundos.`);
            }
        } catch (error) {
            const backendMsg = error?.response?.data?.error || "Ocurrió un error inesperado.";
            setMsgColor("error");
            setMsgPass(`${backendMsg}. Serás desconectado en 5 segundos.`);
        } finally {
            setLoggingOut(true);
            setTimeout(() => { reset(); logout(); }, 5000);
        }
    };

    const EyeToggle = ({ show, setShow }) => (
        <InputAdornment position="end">
            <IconButton onClick={() => setShow(!show)} edge="end" size="small" disabled={isSubmitted}>
                {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
            </IconButton>
        </InputAdornment>
    );

    return (
        <Box>
            {/* ── Form ── */}
            {!loggingOut && (
                <Grid container spacing={2.5} justifyContent="center">
                    <Grid item xs={12} sm={8}>
                        <TextField
                            label="Contraseña Actual"
                            type={showCliPass ? "text" : "password"}
                            fullWidth disabled={isSubmitted}
                            placeholder="••••••••"
                            {...register("cli_pass", { required: "Este campo es obligatorio" })}
                            error={!!errors.cli_pass}
                            helperText={errors.cli_pass?.message}
                            sx={inputSx}
                            InputProps={{ endAdornment: <EyeToggle show={showCliPass} setShow={setShowCliPass} /> }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={8}>
                        <TextField
                            label="Nueva Contraseña"
                            type={showNewPass ? "text" : "password"}
                            fullWidth disabled={isSubmitted}
                            placeholder="Mínimo 8 caracteres"
                            {...register("pass", {
                                required: "Este campo es obligatorio",
                                minLength: { value: 8, message: "Mínimo 8 caracteres" },
                                pattern: { value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/, message: "Debe contener mayúscula, minúscula y número" },
                                validate: (v) => {
                                    const weak = ["123456", "password", "qwerty", "12345678", "abc123"];
                                    return !weak.includes(v) || "Contraseña demasiado común";
                                }
                            })}
                            error={!!errors.pass}
                            helperText={errors.pass?.message}
                            sx={inputSx}
                            InputProps={{ endAdornment: <EyeToggle show={showNewPass} setShow={setShowNewPass} /> }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={8}>
                        <TextField
                            label="Repetir Contraseña"
                            type={showConfirmPass ? "text" : "password"}
                            fullWidth disabled={isSubmitted}
                            placeholder="••••••••"
                            {...register("pass_confirm", {
                                required: "Este campo es obligatorio",
                                validate: (v) => v === watch("pass") || "Las contraseñas no coinciden",
                            })}
                            error={!!errors.pass_confirm}
                            helperText={errors.pass_confirm?.message}
                            sx={inputSx}
                            InputProps={{ endAdornment: <EyeToggle show={showConfirmPass} setShow={setShowConfirmPass} /> }}
                        />
                    </Grid>

                    {/* ── Strength indicator ── */}
                    {passwordStrength > 0 && (
                        <Grid item xs={12} sm={8}>
                            <Box sx={{
                                p: 1.5, borderRadius: 'var(--radius-md)',
                                backgroundColor: 'var(--bg-subtle)', border: '1px solid var(--border)',
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                                    <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                        Seguridad de la contraseña
                                    </Typography>
                                    <Typography sx={{
                                        fontFamily: 'var(--font-heading)', fontSize: '0.75rem', fontWeight: 700,
                                        color: getStrengthCSSColor(),
                                    }}>
                                        {passwordStrengthLabel}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={(passwordStrength / 5) * 100}
                                    color={getStrengthColor()}
                                    sx={{
                                        height: 6, borderRadius: 'var(--radius-full)',
                                        backgroundColor: 'var(--border)',
                                        '& .MuiLinearProgress-bar': { borderRadius: 'var(--radius-full)' },
                                    }}
                                />
                            </Box>
                        </Grid>
                    )}
                </Grid>
            )}

            {/* ── Submit button ── */}
            {!loggingOut && (
                <Box mt={3} display="flex" justifyContent="center">
                    <Button
                        variant="contained"
                        onClick={handleSubmit(handleChangePassword)}
                        disabled={isSubmitted}
                        startIcon={<LockResetOutlinedIcon sx={{ fontSize: 18 }} />}
                        sx={{
                            borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-heading)',
                            fontWeight: 700, fontSize: '0.9rem', textTransform: 'none',
                            backgroundColor: 'var(--accent)', px: 4, py: 1.25,
                            boxShadow: '0 4px 12px rgba(233,69,96,0.3)',
                            '&:hover': { backgroundColor: 'var(--accent-hover)', transform: 'translateY(-1px)' },
                            '&:active': { transform: 'scale(0.98)' },
                            '&:disabled': { backgroundColor: 'var(--border)', boxShadow: 'none' },
                        }}
                    >
                        Cambiar Contraseña
                    </Button>
                </Box>
            )}

            {/* ── Message ── */}
            {msgPass && (
                <Box mt={3}>
                    <Alert
                        severity={msgColor}
                        sx={{
                            borderRadius: 'var(--radius-md)',
                            fontFamily: 'var(--font-body)',
                            fontSize: '0.85rem',
                        }}
                    >
                        {msgPass}
                    </Alert>
                </Box>
            )}

            {/* ── Logging out ── */}
            {loggingOut && (
                <Box mt={3} sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: 1.5, p: 2, borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--bg-subtle)', border: '1px solid var(--border)',
                }}>
                    <CircularProgress size={20} sx={{ color: 'var(--accent)' }} />
                    <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        Cerrando sesión...
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default ChangePass;